import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { stripHtmlTags } from "../utils/stripHtmlTags";
import categoriesApi from "../api/categoriesApi";
import articleApi from "../api/articleApi";
import renderPaginationButtons from "../components/pagination";
import { getTimeAgo } from "../utils/getTimeAgo";
import { useNavigate } from "react-router-dom";
import { useUser } from "../helpers/userContext";
import ImageWithFallback from "../helpers/ImageWithFallback";
import PortalLogoFallback from "../helpers/PortalLogoFallback";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentAlt } from "@fortawesome/free-solid-svg-icons";
import { IoImageOutline } from "react-icons/io5";
import BackButton from "../components/BackButton";

// Lazy load components
const LikeButton = lazy(() => import("../components/likeButton"));

// Skeleton components for loading states
const ArticleCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 shadow-md overflow-hidden animate-pulse">
    {/* Mobile skeleton */}
    <div className="block sm:hidden">
      <div className="relative w-full h-64 bg-gray-200 dark:bg-gray-700"></div>
      <div className="px-2 py-4">
        <div className="flex items-start mb-4">
          <div className="w-1 h-4 bg-gray-300 dark:bg-gray-600 mr-2 mt-1"></div>
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
        </div>
        <div className="flex justify-end">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-8 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    {/* Desktop skeleton */}
    <div className="hidden sm:block">
      <div className="relative w-full h-64 bg-gray-200 dark:bg-gray-700"></div>
      <div className="p-4">
        <div className="flex items-start mb-4">
          <div className="w-1 h-5 bg-gray-300 dark:bg-gray-600 mr-2 mt-1"></div>
          <div className="flex-1 space-y-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
          </div>
        </div>
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        <div className="flex justify-end">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CategoryPageSkeleton = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse mb-2"></div>
      </div>
      <div className="w-full h-0.5 mb-10 bg-gray-200 dark:bg-gray-700"></div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(18)].map((_, i) => (
          <ArticleCardSkeleton key={i} />
        ))}
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-center items-center mt-6 space-x-2">
        <div className="flex space-x-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Lazy article card component
const LazyArticleCard = ({ article, navigate, index = 0, likedStatus }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const portalLogo = article.portal?.logo_url || "";
  const portalName = article.portal?.name || "";
  const articleLink = `/article/${article.id}?categoryId=${article?.categories_related?.[0]}`;
  const commentsCount = article.comments?.length || 0;
  
  // Determine priority and delay based on index
  const isPriority = index < 3; // First 3 images get priority loading
  const delay = isPriority ? 0 : Math.min(index * 100, 1000); // Stagger non-priority images
  
  // Debug portal logo
  if (!portalLogo && article.portal) {
    console.log("No logo URL found for portal:", article.portal);
  }

  return (
    <a
      href={articleLink}
      className={`relative bg-white dark:bg-gray-800 shadow-md overflow-hidden transform ease-in-out cursor-pointer group block image-staggered`}
      style={{ animationDelay: `${index * 50}ms` }}
      onClick={(e) => {
        if (e.target.closest(".portal-logo")) return;
        // Only prevent default if it's a left click (not right click or middle click)
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigate(articleLink);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    >
      {/* Mobile version */}
      <div className="block sm:hidden">
        <div className="relative w-full h-64 overflow-hidden">
          <ImageWithFallback
            src={article.thumbnail || "https://via.placeholder.com/600x400"}
            className="w-full h-full object-cover"
            alt={article.title}
            priority={isPriority}
            delay={delay}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            fallback={
              <IoImageOutline className="w-full h-full object-cover bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" />
            }
          />

          {/* Gradient overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-800 via-white/50 dark:via-gray-800/50 to-transparent"
            style={{
              background: "linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 30%, transparent 70%)",
              pointerEvents: "none",
            }}
          ></div>

          <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
            <div className="grid grid-cols-3 items-center">
              <div className="flex justify-start">
                <div className="w-16 h-5 flex items-center">
                  <ImageWithFallback
                    src={portalLogo}
                    className="h-full w-16 object-contain"
                    alt={portalName}
                    priority={isPriority}
                    fallback={<PortalLogoFallback portalName={portalName} isOverlay={false} />}
                  />
                </div>
              </div>
              <div className="flex justify-center">
                <span className="text-xs text-gray-800 dark:text-gray-200 font-semibold">
                  {getTimeAgo(article.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-2">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-2 flex items-start line-clamp-2">
            <div
              style={{
                width: "4px",
                minWidth: "4px",
                height: "14px",
                backgroundColor: "#DC2626",
                marginRight: "8px",
                marginTop: "4px",
                flexShrink: 0,
              }}
            ></div>
            <span className="mb-5 line-clamp-2">{article.title}</span>
          </h3>
          <span className="mb-5 line-clamp-2 text-gray-700 dark:text-gray-300">
            {stripHtmlTags(article.content)}
          </span>
          <div className="flex justify-end items-center">
            <div className="flex items-center">
              <Suspense fallback={<div className="w-8 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>}>
                <LikeButton
                  articleId={article.id}
                  initialLikeStatus={likedStatus !== undefined ? likedStatus : article.liked}
                  likesCount={article.likes_count || 0}
                />
              </Suspense>
              <FontAwesomeIcon icon={faCommentAlt} className="text-gray-500 dark:text-gray-400 mr-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{commentsCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop version */}
      <div className="hidden sm:block relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden cursor-pointer rounded-t-none group">
        <div className="relative w-full h-64 overflow-hidden">
          <ImageWithFallback
            src={article.thumbnail || "https://via.placeholder.com/600x400"}
            className="w-full h-full object-cover"
            alt={article.title}
            priority={isPriority}
            delay={delay}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            fallback={
              <IoImageOutline className="w-full h-full object-cover bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" />
            }
          />

          {/* Gradient overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-800 via-white/50 dark:via-gray-800/50 to-transparent"
            style={{
              background: "linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 30%, transparent 70%)",
              pointerEvents: "none",
            }}
          ></div>

          <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
            <div className="grid grid-cols-3 items-center">
              <div className="flex justify-start">
                <div className="w-16 h-5 flex items-center">
                  <ImageWithFallback
                    src={portalLogo}
                    className="h-full w-16 object-contain"
                    alt={portalName}
                    priority={isPriority}
                    fallback={<PortalLogoFallback portalName={portalName} isOverlay={false} />}
                  />
                </div>
              </div>
              <div className="flex justify-center">
                <span className="text-xs text-gray-800 dark:text-gray-200 font-semibold">
                  {getTimeAgo(article.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-2xl sm:text-xl font-bold text-gray-900 dark:text-white flex items-start group-hover:underline transition-all">
            <div
              style={{
                width: "4px",
                minWidth: "4px",
                height: "18px",
                backgroundColor: "#DC2626",
                marginRight: "8px",
                marginTop: "4px",
                flexShrink: 0,
              }}
            ></div>
            <span className="line-clamp-2">{article.title}</span>
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3 text-sm sm:text-base">
            {stripHtmlTags(article.content)}
          </p>

          <div className="flex justify-end items-center">
            <div className="flex items-center">
              <Suspense fallback={<div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>}>
                <LikeButton
                  articleId={article.id}
                  initialLikeStatus={likedStatus !== undefined ? likedStatus : article.liked}
                  likesCount={article.likes_count || 0}
                />
              </Suspense>
              <FontAwesomeIcon icon={faCommentAlt} className="text-gray-500 dark:text-gray-400 mr-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{commentsCount}</p>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

const CategoryArticle = () => {
  const navigate = useNavigate(); // For navigation
  const { categoryName } = useParams(); // Category name from URL
  const [searchParams, setSearchParams] = useSearchParams(); // For URL parameters
  const { token } = useUser();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryData, setCategoryData] = useState(null);
  const [articleLikes, setArticleLikes] = useState({});
  const [isLoadingLikes, setIsLoadingLikes] = useState(false);
  const previousCategoryName = useRef(categoryName);
  
  // Get page from URL or default to 1
  const page = parseInt(searchParams.get('page')) || 1;
  
  // Redirect to page 1 if invalid page number
  useEffect(() => {
    const urlPage = searchParams.get('page');
    if (urlPage && (isNaN(parseInt(urlPage)) || parseInt(urlPage) < 1)) {
      setSearchParams({ page: '1' });
    }
  }, [searchParams.get('page')]); // Only depend on the actual page value

  // Handle scroll restoration for browser back navigation
  useEffect(() => {
    const handlePopState = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', handlePopState);
    
    // Also handle when page loads after back navigation
    if (window.performance && window.performance.navigation.type === window.performance.navigation.TYPE_BACK_FORWARD) {
      window.scrollTo(0, 0);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const pageSize = 18; // Fixed page size

  useEffect(() => {
    // Reset to the first page only when category actually changes
    if (previousCategoryName.current !== categoryName) {
      setSearchParams({ page: '1' });
      previousCategoryName.current = categoryName;
    }
  }, [categoryName]); // Remove setSearchParams from dependencies



  // Fetch articles whenever categoryName or page changes
useEffect(() => {
  let isMounted = true; // Add mounted flag to prevent state updates after unmount

  const fetchArticles = async () => {
    if (!categoryName) {
      setArticles([]);
      setLoading(false);
      return;
    }

    try {
      // Show page loading for subsequent pages, full loading for initial load
      const currentPage = parseInt(new URLSearchParams(window.location.search).get('page')) || 1;
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      setError(null);

      // Clear the articles when the categoryName changes to prevent showing the old articles
      setArticles([]); // Clear articles immediately when category changes

      const { data } = await categoriesApi.getArticlesByCategoryName(
        categoryName,
        {
          page,
          page_size: pageSize,
        }
      );

      // Only update state if component is still mounted
      if (isMounted) {
        if (!data || !data.results) {
          setArticles([]);
          return;
        }

        const { count, results, category } = data;

        // Set category data from the API response
        if (category) {
          setCategoryData(category);
          console.log("Category data loaded:", category);
        }

        // Ensure we're setting a fresh array of articles, not appending
        setArticles(
          [...new Set(results.map((article) => article.id))].map((id) =>
            results.find((article) => article.id === id)
          )
        );

        const calculatedTotalPages = Math.ceil(count / pageSize);
        setTotalPages(calculatedTotalPages);
        
        // Check if current page exceeds total pages and redirect to last page
        if (page > calculatedTotalPages && calculatedTotalPages > 0) {
          setSearchParams({ page: calculatedTotalPages.toString() });
          return;
        }
      }
    } catch (error) {
      if (isMounted) {
        console.error("Error fetching articles:", error);
        setArticles([]);
        setError("Failed to load articles.");
      }
    } finally {
      if (isMounted) {
        setLoading(false);
        setPageLoading(false);
      }
    }
  };

  fetchArticles();

  // Cleanup function
  return () => {
    isMounted = false; // Prevent state updates after unmount
  };
}, [categoryName, page, pageSize]); // Add pageSize to dependencies

  // Fetch liked status for all visible articles
  useEffect(() => {
    const fetchLikedStatus = async () => {
      if (!token || !articles || articles.length === 0) {
        setArticleLikes({});
        return;
      }

      // Collect all article IDs from current page
      const allArticleIds = articles
        .filter(article => article?.id)
        .map(article => article.id);

      if (allArticleIds.length === 0) return;

      setIsLoadingLikes(true);
      try {
        const response = await articleApi.getLikedByUser(allArticleIds);
        if (response?.data) {
          setArticleLikes(response.data);
        }
      } catch (error) {
        console.error("Error fetching liked status:", error);
        setArticleLikes({});
      } finally {
        setIsLoadingLikes(false);
      }
    };

    fetchLikedStatus();
  }, [token, articles]); // Run when token or articles change

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setSearchParams({ page: newPage.toString() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };



  if (loading) {
    return <CategoryPageSkeleton />;
  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:col-span-3">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl border-b-2 border-gray-800 dark:border-gray-300 pb-2 font-bold text-gray-900 dark:text-white flex items-center">
              {(categoryData?.name || categoryName).toUpperCase()}
            </h2>
            <BackButton className="mb-2" />
          </div>
          <div className="w-full h-0.5 mb-10 bg-gray-200 dark:bg-gray-700"></div>

          {error && <p className="text-red-500 dark:text-red-400">{error}</p>}

          {!error && articles.length === 0 && !pageLoading && (
            <p className="text-gray-500 dark:text-gray-400">Nuk ka artikuj për këtë kategori.</p>
          )}

          {pageLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(pageSize)].map((_, i) => (
                <ArticleCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 articles-grid">
                             {articles.map((article, index) => (
                 <Suspense key={article.id} fallback={<ArticleCardSkeleton />}>
                   <LazyArticleCard
                     article={article}
                     navigate={navigate}
                     index={index}
                     likedStatus={articleLikes[article.id?.toString()]}
                   />
                 </Suspense>
               ))}
             </div>
           )}

          <div className="flex justify-center items-center mt-6 space-x-2">
            {renderPaginationButtons({
              totalPages,
              currentPage: page,
              handlePageChange,
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryArticle;
